import React from "react";
import { useTranslation } from "react-i18next";

/**
 * The dish, as the guest sees it straight off the screen QR.
 *
 * Deep-linked items are shown even when they are outside their daypart window
 * (`live:false`): the QR is photographed while the dish is on the screen and
 * opened minutes or hours later, so "not found" would be a lie about a real
 * dish. It stays orderable while `available` is true — that mirrors what the
 * server will actually accept for an explicit item id — and only the "on the
 * screen right now" pill goes away.
 */

/**
 * Price as a perforated order-ticket chip. The notches are two circles punched
 * in the honey gradient, painted in the surrounding surface colour so the chip
 * reads as torn from a real ticket roll at any background.
 *
 * The amount is always mono and always Latin digits: the chip is set in IBM
 * Plex Mono, which has no Arabic cut, and a currency symbol like "د.أ" inside a
 * mono run renders as a fallback-font island in the middle of the number.
 */
function TicketChip({ amount, was, currency, unitHint }) {
  return (
    <span className="ss-ticket-wrap">
      <span className="ss-ticket">
        <span className="ss-ticket-notch start" aria-hidden="true" />
        <span className="ss-ticket-body">
          <span className="ss-ticket-cur">{currency}</span>
          <span className="ss-ticket-amt">{amount}</span>
          {was != null && <s className="ss-ticket-was">{was}</s>}
        </span>
        <span className="ss-ticket-notch end" aria-hidden="true" />
      </span>
      {/* Only shown once the chip stops being the unit price, so the guest can
          see WHY it changed rather than watching a number move on its own. */}
      {unitHint && <span className="ss-ticket-unit" dir="ltr">{unitHint}</span>}
    </span>
  );
}

/** Money for display: 2 decimals, Latin digits, never a floating-point tail. */
const money = (n) => (Math.round((Number(n) || 0) * 100) / 100).toFixed(2);

export default function ItemView({ item, currency, qty, onQty, fromScreen }) {
  const { t, i18n } = useTranslation();
  const isAr = (i18n.resolvedLanguage || i18n.language || "en").startsWith("ar");

  // Title in the reader's language; the OTHER language rides above as a small
  // mono eyebrow. Bilingual venues name the same dish twice, and stacking them
  // as equals makes the card read like two products.
  const title = (isAr && item.name_ar) || item.name;
  const eyebrow = isAr ? item.name : item.name_ar;
  const description = (isAr && item.description_ar) || item.description;

  // The chip is the LINE TOTAL, not the unit price: it sits directly beside the
  // stepper, so a number that ignores quantity reads as the price of what you
  // are about to send — which it isn't once qty > 1. Any struck-through original
  // scales with it, or the discount would appear to shrink as you add items.
  const unit = Number(item.discount_price ?? item.price) || 0;
  const unitWas = item.discount_price != null ? Number(item.price) || 0 : null;
  const lineTotal = money(unit * qty);
  const lineWas = unitWas != null ? money(unitWas * qty) : null;
  const unitHint = qty > 1 ? `${qty} × ${currency} ${money(unit)}` : null;

  return (
    <section className="ss-item">
      <div className="ss-item-photo">
        {item.image_url ? (
          <img src={item.image_url} alt={title} loading="eager" />
        ) : (
          <div className="ss-item-photo-empty" aria-hidden="true">🍽️</div>
        )}

        {/* Only claim "scanned from the screen" when they actually arrived by
            deep link AND the item is still up there — the pill is a moment of
            recognition, and it turns into a small lie once the daypart ends. */}
        {fromScreen && item.live && (
          <span className="ss-item-pill">
            <span className="ss-item-pill-dot" aria-hidden="true" />
            {t("scan_stay.order.from_screen")}
          </span>
        )}
      </div>

      <div className="ss-item-body">
        {eyebrow && <div className="ss-item-eyebrow">{eyebrow}</div>}
        <h1 className="ss-item-title">{title}</h1>
        {description && <p className="ss-item-desc">{description}</p>}

        <div className="ss-item-row">
          <TicketChip amount={lineTotal} was={lineWas} currency={currency} unitHint={unitHint} />

          <div className="ss-qty" role="group" aria-label={t("scan_stay.order_qty")}>
            <button
              type="button"
              onClick={() => onQty(Math.max(1, qty - 1))}
              disabled={qty <= 1}
              aria-label={t("scan_stay.order.qty_less")}
            >
              −
            </button>
            <span className="ss-qty-n" aria-live="polite">{qty}</span>
            <button
              type="button"
              onClick={() => onQty(Math.min(99, qty + 1))}
              disabled={qty >= 99}
              aria-label={t("scan_stay.order.qty_more")}
            >
              +
            </button>
          </div>
        </div>

        {!item.live && item.available && (
          <div className="ss-item-note">{t("scan_stay.order.not_live_note")}</div>
        )}
      </div>
    </section>
  );
}
